import {
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SETS_GET_ORG_STRUCTURE } from 'constants/requestUrls';
import { INTERNAL_SERVER_ERROR, UNAUTHORIZED } from 'constants/statusCodes';
import * as queryString from 'query-string';
import { logoutUser } from 'store/slices/auth/slice';
import AGENT_TEMPLATES_ADMIN_LIST_TAG, {
  AGENT_TEMPLATES_ADMIN_RECORD_TAG,
  AGENT_TEMPLATES_ADMIN_VERSIONS_TAG,
} from 'store/tags/agentTemplatesAdmin';
import AI_CHATS_LIST_TAG, { AI_CHAT_MESSAGES_TAG } from 'store/tags/aiChats';
import AUTHENTICATED_USER from 'store/tags/auth';
import COMPANY_TAG, { COMPANY_PERMISSION_TAG, COMPANY_USER } from 'store/tags/companies';
import DATA_SETS_PROFIT_CENTER_LIST_TAG, {
  DATA_CUSTOMERS_LIST_TAG,
  DATA_PRODUCTS_LIST_TAG,
  DATA_SETS_CUSTOMERS_RECORD_TAG,
  DATA_SETS_OWN_OPERATION_LIST_TAG,
  DATA_SETS_OWN_OPERATION_RECORD_TAG,
  DATA_SETS_PRODUCTS_RECORD_TAG,
  DATA_SETS_PROFIT_CENTER_RECORD_TAG,
  DATA_SETS_SUPPLIERS_RECORD_TAG,
  DATA_SETS_UNIQ_GEO_STRUCTURES_TAG,
  DATA_SETS_UNIQ_ORG_STRUCTURES_TAG,
  DATA_SUPPLIERS_LIST_TAG,
} from 'store/tags/dataSets';
import TEMPLATES_LIST_TAG, { TEMPLATES_REQUEST_TAG } from 'store/tags/iroStandardsTags';
import LAYOUTS_MA, { LAYOUTS_MA_RECORD, LAYOUTS_MA_RECORD_TYPE, LAYOUTS_MA_RECORD_TYPE_DETAILS } from 'store/tags/layoutsMA';
import NOTIFICATIONS_LIST_TAG from 'store/tags/notifications';
import { SPECIFIC_DETAILS_TAG, SPECIFIC_LIST_TAG } from 'store/tags/specificTags';
import { SPECIFIC_CUSTOM_DETAILS_TAG, SPECIFIC_CUSTOM_LIST_TAG } from 'store/tags/specificTagsCustom';
import { STANDARDS_LIST_TAG } from 'store/tags/standardsTags';
import { STANDARDS_TOPIC_DETAILS_TAG, STANDARDS_TOPIC_TAG } from 'store/tags/standardsTopicTags';
import TASK_MANAGEMENT_LIST_TAG, {
  TASK_MANAGEMENT_ACTIVITIES_TAG, TASK_MANAGEMENT_ATTACHMENTS_TAG,
  TASK_MANAGEMENT_RECORD_TAG,
} from 'store/tags/tasksManagement';
import VC_CUSTOMER_LIST_TAG from 'store/tags/valueChainCustomers';
import VC_AGENT_SESSION_LIST_TAG, {
  VC_AGENT_MONITORING_RESULTS_TAG,
  VC_AGENT_MONITORING_RUN_FINDINGS_TAG,
  VC_AGENT_MONITORING_SCHEDULE_EXECUTIONS_TAG,
  VC_AGENT_MONITORING_SCHEDULE_RUNS_TAG,
  VC_AGENT_MONITORING_SCHEDULES_TAG,
  VC_AGENT_MONITORING_SESSION_EXECUTIONS_TAG,
  VC_AGENT_SESSION_BOM_ITEMS_TAG,
  VC_AGENT_SESSION_BOM_LINE_ITEM_RECORD_TAG,
  VC_AGENT_SESSION_BOM_RECORD_TAG,
  VC_AGENT_SESSION_BOM_TAG,
  VC_AGENT_SESSION_RECORD_TAG,
  VC_AGENT_SESSION_SUPPLIER_DISCOVERY_ITEM_RECORD_TAG,
  VC_AGENT_SESSION_SUPPLIER_DISCOVERY_ITEMS_TAG,
  VC_AGENT_SESSION_SUPPLIER_DISCOVERY_TAG,
  VC_AGENT_SESSION_SUPPLIER_DISCOVERY_UNIT_TAG,
  VC_DRAFT_STRUCTURE_TAG,
} from 'store/tags/valueChainSession';
import VC_SUPPLIER_LIST_TAG from 'store/tags/valueChainSuppliers';
import {
  WATCH_TOWER_CHATS_TAG,
  WATCH_TOWER_COMPANIES_TAG,
  WATCH_TOWER_COMPANY_TAG,
  WATCH_TOWER_INCIDENT_TAG,
  WATCH_TOWER_INCIDENTS_TAG,
  WATCH_TOWER_RUNS_TAG,
  WATCH_TOWER_SCHEDULES_TAG,
  WATCH_TOWER_TOPICS_TAG,
} from 'store/tags/watchTower';
import { refNavigateTo } from 'utils/navigationRef';

import { MFA_ENDPOINTS } from '../../constants/interfaces';
import { setAuthState } from '../slices/auth/slice';
import {
  MATERIAL_ASSESSMENT_MANAGEMENT_LIST_TAG,
  MATERIAL_ASSESSMENT_MANAGEMENT_RECORD_TAG,
  MATERIAL_STAKEHOLDER_BOARDS_TAG,
  MATERIAL_STAKEHOLDER_COMMUNITIES_TAG,
  MATERIAL_STAKEHOLDER_COMPETITORS_TAG,
  MATERIAL_STAKEHOLDER_CUSTOMERS_TAG,
  MATERIAL_STAKEHOLDER_EMPLOYEES_TAG,
  MATERIAL_STAKEHOLDER_GEO_DETAILS_TAG,
  MATERIAL_STAKEHOLDER_GEO_TAG,
  MATERIAL_STAKEHOLDER_INVESTOR_TAG,
  MATERIAL_STAKEHOLDER_MEDIA_TAG,
  MATERIAL_STAKEHOLDER_NGO_TAG,
  MATERIAL_STAKEHOLDER_SUPPLIERS_TAG,
  MATERIALITY_IRO_EMAILS_TAG,
  MATERIALITY_IRO_TEMPLATES_IMPACTS_TAG,
  MATERIALITY_IRO_TEMPLATES_OPPORTUNITIES_TAG,
  MATERIALITY_IRO_TEMPLATES_RISKS_TAG,
  MATERIALITY_RESPONDENT_LIST_TAG,
  MATERIALITY_STAKEHOLDER_ANALYSIS_TAG,
  MATERIALITY_TOPIC_REFINEMENT_TAG,
  MATERIALITY_TOPICS_LIST_DISCLOSURES_DETAILS_TAG,
  MATERIALITY_TOPICS_LIST_DISCLOSURES_TAG,
  MATERIALITY_TOPICS_LIST_TAG,
} from '../tags/materialAssessment';
import {
  VALUE_CHAIN_MAP_ANALYSES_TAG,
  VALUE_CHAIN_MAP_BOARD_COLUMN_NAMES_TAG,
  VALUE_CHAIN_MAP_BOARD_TAG,
  VALUE_CHAIN_MAP_BUSINESS_STRUCTURE_TAG,
  VALUE_CHAIN_MAP_DETAILS_TAG,
  VALUE_CHAIN_MAP_LIST_TAG,
  VALUE_CHAIN_MAP_ORG_STRUCTURE_TAG,
} from '../tags/valueChainMap';

import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from 'store/types/TStore';
import type IRefreshTokenResponse from '../slices/auth/interfaces/IRefreshTokenResponse';
import type IBaseQueryResponse from '../types/IBaseQueryResponse';

const authBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const {
      accessToken, verifyToken, userAccessToken,
    } = (getState() as RootState).auth;

    const isMfaEndpoint = [...Object.values(MFA_ENDPOINTS)].some((api) => api === endpoint);
    const generalAccessToken = userAccessToken || accessToken;

    if (isMfaEndpoint && verifyToken) {
      headers.set('authorization', `Bearer ${verifyToken}`);
      return headers;
    }

    if (generalAccessToken) {
      headers.set('authorization', `Bearer ${generalAccessToken}`);
    }
    return headers;
  },
  paramsSerializer: (params) => queryString.default.stringify(params),
});

const mutex = new Mutex();
export const appBaseQueryWithReauth: BaseQueryFn<
string | FetchArgs,
unknown,
FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await authBaseQuery(args, api, extraOptions);

  if (result.error) {
    const { status } = result.error;
    if (status === UNAUTHORIZED) {
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();

        try {
          const state = api.getState();
          const { refreshToken, userRefreshToken } = (state as RootState).auth;

          const generalRefreshToken = userRefreshToken || refreshToken;
          if (generalRefreshToken) {
            const { data: refreshData } = await authBaseQuery({
              url: '/auth/refresh/',
              method: 'PATCH',
              body: {
                token: generalRefreshToken,
              },
            }, api, extraOptions);
            const { data } = (refreshData as IBaseQueryResponse<IRefreshTokenResponse>) || {};

            if (data) {
              const { accessToken, userAccessToken } = data;
              api.dispatch(setAuthState({
                accessToken: userAccessToken || accessToken,
              }));

              const resultBaseQuery = await authBaseQuery(args, api, extraOptions);

              result = resultBaseQuery;

              if (resultBaseQuery?.meta?.response?.status === UNAUTHORIZED) {
                api.dispatch(logoutUser());
                refNavigateTo(AppRoutes.login);
              }
            } else {
              api.dispatch(logoutUser());
              refNavigateTo(AppRoutes.login);
            }
          } else {
            api.dispatch(logoutUser());
            refNavigateTo(AppRoutes.login);
          }
        } finally {
          release();
        }
      } else {
        await mutex.waitForUnlock();
        result = await authBaseQuery(args, api, extraOptions);
      }
    } else if (
      +status >= INTERNAL_SERVER_ERROR
        || ('originalStatus' in result.error && +result.error.originalStatus >= INTERNAL_SERVER_ERROR)
    ) {
      // Handle server errors here
    }
  }
  return result;
};

const mainApi = createApi({
  reducerPath: 'mainApi',
  tagTypes: [
    AUTHENTICATED_USER,
    COMPANY_TAG,
    COMPANY_PERMISSION_TAG,
    COMPANY_USER,
    VALUE_CHAIN_MAP_LIST_TAG,
    VALUE_CHAIN_MAP_ANALYSES_TAG,
    VALUE_CHAIN_MAP_DETAILS_TAG,
    VALUE_CHAIN_MAP_BUSINESS_STRUCTURE_TAG,
    VALUE_CHAIN_MAP_BOARD_TAG,
    VALUE_CHAIN_MAP_ORG_STRUCTURE_TAG,
    VALUE_CHAIN_MAP_BOARD_COLUMN_NAMES_TAG,
    TASK_MANAGEMENT_LIST_TAG,
    TASK_MANAGEMENT_RECORD_TAG,
    TASK_MANAGEMENT_ATTACHMENTS_TAG,
    TASK_MANAGEMENT_ACTIVITIES_TAG,
    NOTIFICATIONS_LIST_TAG,
    MATERIAL_ASSESSMENT_MANAGEMENT_LIST_TAG,
    MATERIAL_ASSESSMENT_MANAGEMENT_RECORD_TAG,
    MATERIAL_STAKEHOLDER_INVESTOR_TAG,
    MATERIAL_STAKEHOLDER_BOARDS_TAG,
    MATERIAL_STAKEHOLDER_EMPLOYEES_TAG,
    MATERIAL_STAKEHOLDER_COMMUNITIES_TAG,
    MATERIAL_STAKEHOLDER_NGO_TAG,
    MATERIAL_STAKEHOLDER_MEDIA_TAG,
    STANDARDS_LIST_TAG,
    STANDARDS_TOPIC_TAG,
    STANDARDS_TOPIC_DETAILS_TAG,
    SPECIFIC_LIST_TAG,
    SPECIFIC_DETAILS_TAG,
    SPECIFIC_CUSTOM_LIST_TAG,
    SPECIFIC_CUSTOM_DETAILS_TAG,
    MATERIAL_STAKEHOLDER_GEO_TAG,
    MATERIAL_STAKEHOLDER_GEO_DETAILS_TAG,
    VC_SUPPLIER_LIST_TAG,
    MATERIAL_STAKEHOLDER_SUPPLIERS_TAG,
    MATERIAL_STAKEHOLDER_CUSTOMERS_TAG,
    VC_CUSTOMER_LIST_TAG,
    MATERIALITY_TOPICS_LIST_TAG,
    LAYOUTS_MA,
    LAYOUTS_MA_RECORD,
    LAYOUTS_MA_RECORD_TYPE,
    MATERIAL_STAKEHOLDER_COMPETITORS_TAG,
    MATERIALITY_STAKEHOLDER_ANALYSIS_TAG,
    MATERIALITY_TOPICS_LIST_DISCLOSURES_TAG,
    MATERIALITY_TOPICS_LIST_DISCLOSURES_DETAILS_TAG,
    MATERIALITY_IRO_TEMPLATES_IMPACTS_TAG,
    MATERIALITY_IRO_TEMPLATES_RISKS_TAG,
    MATERIALITY_IRO_TEMPLATES_OPPORTUNITIES_TAG,
    MATERIALITY_IRO_EMAILS_TAG,
    TEMPLATES_LIST_TAG,
    TEMPLATES_REQUEST_TAG,
    AGENT_TEMPLATES_ADMIN_LIST_TAG,
    AGENT_TEMPLATES_ADMIN_RECORD_TAG,
    AGENT_TEMPLATES_ADMIN_VERSIONS_TAG,
    MATERIALITY_TOPIC_REFINEMENT_TAG,
    MATERIALITY_RESPONDENT_LIST_TAG,
    LAYOUTS_MA_RECORD_TYPE_DETAILS,
    DATA_SETS_GET_ORG_STRUCTURE,
    DATA_SETS_PROFIT_CENTER_RECORD_TAG,
    DATA_SETS_PROFIT_CENTER_LIST_TAG,
    DATA_SETS_OWN_OPERATION_RECORD_TAG,
    DATA_SETS_OWN_OPERATION_LIST_TAG,
    DATA_SETS_UNIQ_ORG_STRUCTURES_TAG,
    DATA_SETS_UNIQ_GEO_STRUCTURES_TAG,
    DATA_SETS_SUPPLIERS_RECORD_TAG,
    DATA_SUPPLIERS_LIST_TAG,
    DATA_SETS_CUSTOMERS_RECORD_TAG,
    DATA_CUSTOMERS_LIST_TAG,
    DATA_SETS_PRODUCTS_RECORD_TAG,
    DATA_PRODUCTS_LIST_TAG,
    VC_AGENT_SESSION_LIST_TAG,
    VC_AGENT_SESSION_RECORD_TAG,
    VC_DRAFT_STRUCTURE_TAG,
    VC_AGENT_SESSION_BOM_TAG,
    VC_AGENT_SESSION_BOM_RECORD_TAG,
    VC_AGENT_SESSION_BOM_ITEMS_TAG,
    VC_AGENT_SESSION_BOM_LINE_ITEM_RECORD_TAG,
    VC_AGENT_SESSION_SUPPLIER_DISCOVERY_TAG,
    VC_AGENT_SESSION_SUPPLIER_DISCOVERY_UNIT_TAG,
    VC_AGENT_SESSION_SUPPLIER_DISCOVERY_ITEMS_TAG,
    VC_AGENT_SESSION_SUPPLIER_DISCOVERY_ITEM_RECORD_TAG,
    VC_AGENT_MONITORING_SCHEDULES_TAG,
    VC_AGENT_MONITORING_SCHEDULE_RUNS_TAG,
    VC_AGENT_MONITORING_SCHEDULE_EXECUTIONS_TAG,
    VC_AGENT_MONITORING_SESSION_EXECUTIONS_TAG,
    VC_AGENT_MONITORING_RUN_FINDINGS_TAG,
    VC_AGENT_MONITORING_RESULTS_TAG,
    AI_CHATS_LIST_TAG,
    AI_CHAT_MESSAGES_TAG,
    WATCH_TOWER_COMPANIES_TAG,
    WATCH_TOWER_COMPANY_TAG,
    WATCH_TOWER_TOPICS_TAG,
    WATCH_TOWER_SCHEDULES_TAG,
    WATCH_TOWER_INCIDENTS_TAG,
    WATCH_TOWER_INCIDENT_TAG,
    WATCH_TOWER_RUNS_TAG,
    WATCH_TOWER_CHATS_TAG,
  ],
  baseQuery: appBaseQueryWithReauth,
  endpoints: () => ({}),
});

export default mainApi;
